import { useContext, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { Trans } from '@lingui/react';
import styled from 'styled-components';
import { parse } from 'date-fns/parse';
import { isFuture } from 'date-fns/isFuture';
import Button from '@components/buttons';
import { Flex } from '@components/layout/Grid';
import { useLiveEventCodesLazyQuery, PromotionCode } from '@gql/generated';
import { UserContext } from '@lib/contexts/UserProvider';
import LanguageContext from '@lib/contexts/languageContext';
import { HasValidLiveCodes } from '@lib/utils';

const Card = styled(Flex)`
  color: #243240;
  border-radius: 4px;
  background-color: #f0f5fb;

  & ~ .Live--LiveCard:nth-of-type(12n):last-of-type {
    display: none;
  }
`;

const Title = styled.p`
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  margin: 0 0 10px;
`;

const Body = styled.p`
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  margin: 0 0 10px;
`;

const SubTitle = styled.p`
  font-size: 14px;
  line-height: 22px;
  margin: 0 0 10px;
  opacity: 0.8;
`;

const checkLiveCodeValidity = (liveEvent: PromotionCode) => {
  //
  const expirationDate = parse(
    liveEvent.expirationDate,
    'yyyy-MM-dd',
    new Date()
  );
  return (
    isFuture(expirationDate) && liveEvent.usedIn?.length < liveEvent.usageLimit
  );
};

export const LiveCodesAvailableCard = () => {
  const lang = useContext(LanguageContext);
  const [currentUser] = useContext(UserContext);
  const [loadLiveEventCodes, { data }] = useLiveEventCodesLazyQuery();
  //

  useEffect(() => {
    if (currentUser?.id) {
      loadLiveEventCodes();
    }
  }, [currentUser?.id]);

  const hasAvailableLiveCodes = useMemo(
    () => HasValidLiveCodes(data?.liveEventCodes),
    [data?.liveEventCodes]
  );

  if (!hasAvailableLiveCodes) {
    return null;
  }

  return (
    <Card
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      px={[10, 10, 20]}
      py={40}
    >
      {/*  */}
      <Title>
        <Trans id="live.listings.live_codes_card.title" />
      </Title>
      <Body>
        <Trans id="live.listings.live_codes_card.description" />
      </Body>
      <SubTitle>
        <Trans id="live.listings.live_codes_card.description2" />
      </SubTitle>
      <Link href={`/${lang}/me/settings/live-codes`} passHref>
        <Button>
          <Trans id="live.listings.live_codes_card.see_code_btn" />
        </Button>
      </Link>
    </Card>
  );
};
